import {utils, writeFile} from 'xlsx'
import {backendGetRequest} from '../../../../../common/utils/requests'
import {formatDate} from '../../../../../common/utils'
import {SortApi} from '../../../../../shared/hooks/useSort'
import {ApiResponse, FilterValues, PerformanceFeeDeposit, SortableFields} from './types'
import {fromFilters} from './utils'

const EXPORT_PAGE_SIZE = 500

type FetchAllParams = {
  accountId: number
  filters: FilterValues
  sortApi: SortApi<SortableFields>
}

async function fetchPerformanceFeePage(
  accountId: number,
  params: Record<string, string | number | undefined>,
): Promise<ApiResponse> {
  const response = await backendGetRequest(
    `/api/v2/dashboard/accounts/${accountId}/deposits/performance-fee`,
    true,
    params,
  )
  if (!response.ok) {
    throw new Error(`Failed to fetch performance fees for export (${response.status})`)
  }
  return response.json()
}

/** Loads every filtered performance-fee deposit (paginated) for export. */
export async function fetchAllPerformanceFeeDeposits({
  accountId,
  filters,
  sortApi,
}: FetchAllParams): Promise<PerformanceFeeDeposit[]> {
  const filterParams = fromFilters(filters)
  const deposits: PerformanceFeeDeposit[] = []
  let offset = 0
  let totalCount = Infinity

  while (offset < totalCount) {
    const page = await fetchPerformanceFeePage(accountId, {
      ...filterParams,
      ...sortApi.value,
      offset,
      limit: EXPORT_PAGE_SIZE,
    })
    deposits.push(...page.deposits)
    totalCount = page.totalCount
    offset += EXPORT_PAGE_SIZE
    if (page.deposits.length === 0) break
  }

  return deposits
}

type ExportRowsParams = {
  deposits: PerformanceFeeDeposit[]
  offerName: string
  locale: string
  fileName?: string
}

function toExportRows({deposits, offerName, locale}: ExportRowsParams) {
  return deposits.map((deposit) => ({
    Strategy: offerName || '-',
    Follower: deposit.followerName ?? '-',
    Amount: deposit.amount,
    Currency: deposit.currency,
    'Transaction Date':
      deposit.completedAt != null
        ? formatDate(locale, deposit.completedAt, {dateStyle: 'medium', timeStyle: 'short'})
        : '-',
    Reference: deposit.reference ?? String(deposit.id),
  }))
}

export {toExportRows}

/** Downloads the filtered transaction history as an XLSX spreadsheet. */
export function downloadPerformanceFeeHistoryXlsx(params: ExportRowsParams): void {
  const rows = toExportRows(params)
  const sheet = utils.json_to_sheet(rows)
  const workbook = utils.book_new()
  utils.book_append_sheet(workbook, sheet, 'Performance Fees')
  const baseName = params.fileName ?? `performance-fee-history-${Date.now()}`
  writeFile(workbook, `${baseName}.xlsx`)
}
